import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  Chip,
  IconButton,
  Paper,
  Dialog,
  DialogContent,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  Search,
  Add,
} from "@mui/icons-material";
import { EditOutlined } from "@mui/icons-material";
import {getAllVarietiesAction} from "../../store/actions";

import CreateVariety from "./CreateVariety";
import { useDispatch, useSelector } from "react-redux";

export default function VarietyManagement() {
  const dispatch = useDispatch();
  
    // State for modal open/close
  const [openVarietyModal, setOpenVarietyModal] = useState(false);
  const [selectedVarietyId, setSelectedVarietyId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const{successData, loading, } = useSelector((state)=>state.getAllVarietiesReducer); 

  const handleEdit = (varietyId) => {
    if (!varietyId) return;
    setSelectedVarietyId(varietyId);
    setOpenVarietyModal(true);
  }


  useEffect(() => {
    dispatch(getAllVarietiesAction());
  }, [dispatch]);

  const varieties = successData?.data?.varieties || [];
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredVarieties = normalizedSearch
    ? varieties.filter((item) => {
        const fields = [
          item?.name,
          item?.cropName,
        ];
        return fields.some((val) =>
          (val || "").toString().toLowerCase().includes(normalizedSearch)
        );
      })
    : varieties;

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "100%" },
        minHeight: { xs: "100vh", md: "100vh" },
        height: { md: "100vh", lg: "100vh", xl: "100vh" },
        p: { xs: 0.5, sm: 1, md: 0, lg: 0, xl: 0 },
        boxSizing: "border-box",
      }}
    >
      {/* Top Bar moved to cropVariety/index.jsx header */}

      {/* Title */}
      <Typography
      mb={2}
        color="#111827"
      fontSize={18} fontWeight={600}
      >
        Variety Management
      </Typography>

      {/* Filters */}
      <Card
        sx={{
          p: { xs: 2, sm: 2, md: 2, lg: 2 },
          mb: { xs: 3, sm: 4 },
          borderRadius: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 2 },
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <TextField
          placeholder="Search ..."
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ mr: 1 }} />
                </InputAdornment>
              ),
            },
          }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: { xs: "100%", sm: 200, md: 260 ,borderRadius:3} }}
        />
        <Box flexGrow={1} sx={{ display: { xs: "none", sm: "block" } }} />

        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{
            background: "#2563EB",
            textTransform: "none",
            borderRadius: 3,
            px: { xs: 2, sm: 2.5, md: 3 },
            width: { xs: "100%", sm: "auto" },
            mt: { xs: 1, sm: 0 },
          }}
          onClick={() => { setSelectedVarietyId(null); setOpenVarietyModal(true); }}
        >
          Add New Variety
        </Button>

        {/* Modal for Add New Variety */}
        <Dialog
          open={openVarietyModal}
          onClose={() => setOpenVarietyModal(false)}
          maxWidth="md"
          fullWidth
          aria-labelledby="add-variety-dialog"
        >
          <DialogContent sx={{ p: 0 }}>
            <CreateVariety onClose={() => setOpenVarietyModal(false)} varietyId={selectedVarietyId} />
          </DialogContent>
        </Dialog>
      </Card>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: '40vh', sm: '50vh' },
            width: '100%'
          }}
          aria-busy
        >
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <>
          {/* Table Header */}
          <Box
            display={{ xs: "none", sm: "grid" }}
            gridTemplateColumns={{ sm: "3fr 1.5fr 1fr 1fr 1fr" }}
            px={{ xs: 0.5, sm: 2 }}
            mb={{ xs: 0.5, sm: 1 }}
          >
            <Typography fontSize={12}  color="#000000"  fontWeight={600}>
              VARIETY NAME
            </Typography>
            <Typography fontSize={12}  color="#000000" fontWeight={600}>
              CROP NAME
            </Typography>
            <Typography fontSize={12}  color="#000000" fontWeight={600}>
              QTY TYPE
            </Typography>
            <Typography fontSize={12}  color="#000000" fontWeight={600}>
              STATUS
            </Typography>
            <Typography fontSize={12}  color="#000000" fontWeight={600}>
              ACTIONS
            </Typography>
          </Box>

          {/* Rows */}
          {filteredVarieties.map((item, index) => (
            <Paper
              key={item.varietyId}
              elevation={0}
              sx={{
                mb: { xs: 1, sm: 2 },
                p: { xs: 1, sm: 1 },
                borderRadius: 3,
                display: { xs: "flex", sm: "grid" },
                flexDirection: { xs: "column", sm: "unset" },
                gridTemplateColumns: { sm: "3fr 1.5fr 1fr 1fr 1fr" },
                alignItems: "center",
                border: "1px solid #E5E7EB",
                gap: { xs: 1, sm: 0 },
                transition: 'border 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  border: '2px solid #2563EB',
                  boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                },
              }}
            >
              {/* Mobile and Desktop Card Layout: Season name and subtitle at top for all screens, like Crop.jsx */}
              <Box
                sx={{
                  display: { xs: "block", sm: "flex" },
                  width: { xs: "100%", sm: "auto" },
                  mb: { xs: 1, sm: 0 },
                }}
              >
                {/* Top row for all screens: Avatar, season name, subtitle. For xs, match screenshot style. */}
                {/* Mobile: label, avatar, name, subtitle in a row. Desktop: avatar, name, subtitle as before. */}
                <Box
                  display="flex"
                  alignItems="center"
                  mb={{ xs: 1, sm: 2 }}
                  sx={{ flexWrap: 'wrap' }}
                >
                  {/* Mobile layout: label, avatar, name, subtitle in a row */}
                  <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                      <Typography fontWeight={600}  sx={{ mr: 1, whiteSpace: 'nowrap' }}>Variety Name:</Typography>
                      <Typography  fontSize={14} component="span" sx={{ mr: 1, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{item.name}</Typography>
                    </Box>
                    <Chip
                      label={item.status}
                      sx={{ width: 70,...(((item.status || '').toUpperCase() === 'ACTIVE')
                          ? { backgroundColor: '#D1FAE5', color: '#047857' }
                          : { backgroundColor: '#FEE2E2', color: '#B91C1C' }),}}
                      size="small"
                    />
                  </Box>
                  {/* Desktop layout: icons removed, show name only */}
                  <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
                    <Typography fontSize={16}>{item.name}</Typography>
                  </Box>
                </Box>
                {/* Only show below on xs: crop, qty, status, actions */}
                <Box sx={{ display: { xs: "block", sm: "none" } }}>
                  <Box display="flex" mb={1}>
                    <Typography fontWeight={600} sx={{ minWidth: 90 }}>Crop Name:</Typography>
                    <Typography  fontSize={14}>{item.cropName}</Typography>
                  </Box>
                  <Box display="flex" mb={1}>
                    <Typography fontWeight={600} sx={{ minWidth: 90 }}>Qty Type:</Typography>
                    <Typography  fontSize={14}>{item.quantity_type}</Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" justifyContent="flex-end">
                    <Box>
                      <IconButton size="small">
                        <EditOutlined  sx={{ color: "#2563EB", filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} onClick={() => handleEdit(item.varietyId)} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Desktop grid columns */}
              <Typography sx={{ display: { xs: "none", sm: "block" } }}>{item.cropName}</Typography>
              <Typography sx={{ display: { xs: "none", sm: "block" } }}>{item.quantity_type}</Typography>
              <Chip
                label={item.status}
                sx={{
                  width: 80,
                 ...(((item.status || '').toUpperCase() === 'ACTIVE')
                          ? { backgroundColor: '#D1FAE5', color: '#047857' }
                          : { backgroundColor: '#FEE2E2', color: '#B91C1C' }),

                  display: { xs: "none", sm: "inline-flex" },
                }}
                size="small"
              />
              <Box sx={{ display: { xs: "none", sm: "flex" } }}>
                <IconButton size="small">
                  <EditOutlined sx={{ color: "#2563EB", filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} onClick={() => handleEdit(item.varietyId)} />
                </IconButton>
               
              </Box>
            </Paper>
          ))}
        </>
      )}
    </Box>
  );
}

